'use client';

import { ArrowDown, Loader2 } from 'lucide-react';
import { MutableRefObject, startTransition, useCallback, useEffect, useRef, useState } from 'react';

import { ImageData, Message, User } from '@/slw/entities/chat';
import { Button } from '@/slw/shared/ui/button';
import { useChatStore } from '@/slw/shared/lib/firebase/chat-store';

import { MessageItem } from './message-item';
import { ChatSkeleton } from './chat-skeleton';

interface MessageListProps {
  bottomSpan: MutableRefObject<HTMLSpanElement | null>;
  isAtBottom: boolean;
  selectedUser: User | null;
  messages: Message[];
  onActionClick?: (actionType: string, messageIndex: number) => void;
  openModal: (imgUrl: ImageData) => void;
  scrollToBottom: (input?: boolean, delay?: number) => void;
  onLoadMore: () => void;
  loading: boolean;
  hasMore: boolean;
  loadingMore: boolean;
}

const getMessageVariant = (messageName: string, selectedUserName: string) =>
  messageName !== selectedUserName ? 'sent' : 'received';

export const MessageList = ({
  bottomSpan,
  messages,
  selectedUser,
  scrollToBottom,
  onActionClick,
  openModal,
  onLoadMore,
  loading,
  hasMore,
  loadingMore,
}: MessageListProps) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const prevMessagesLength = useRef(messages.length);
  const prevScrollHeight = useRef(0);
  const shouldMaintainScrollPosition = useRef(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const { chatId } = useChatStore();

  useEffect(() => {
    startTransition(() => {
      shouldMaintainScrollPosition.current = false;
      prevMessagesLength.current = 0;
      setIsNearBottom(true);
      setShowScrollButton(false);
      prevScrollHeight.current = 0;
    });
  }, [chatId]);

  const handleScroll = useCallback(() => {
    const container = scrollRef.current;

    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;

    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const isNearBottomNow = distanceFromBottom < 100;

    setIsNearBottom(isNearBottomNow);
    setShowScrollButton(!isNearBottomNow);

    if (scrollTop < 100 && hasMore && !loading) {
      shouldMaintainScrollPosition.current = true;
      prevScrollHeight.current = scrollHeight;
      onLoadMore();
    }
  }, [hasMore, loading, onLoadMore, scrollRef]);

  // Handle scroll position maintenance after loading more messages
  useEffect(() => {
    const container = scrollRef.current;

    if (!container) return;

    if (shouldMaintainScrollPosition.current && messages.length > prevMessagesLength.current) {
      const newScrollHeight = container.scrollHeight;
      const scrollDiff = newScrollHeight - prevScrollHeight.current;

      // Maintain scroll position by adjusting for new content height
      container.scrollTop = container.scrollTop + scrollDiff;

      shouldMaintainScrollPosition.current = false;
    }
  }, [messages.length, scrollRef]);

  // Auto-scroll to bottom when new messages arrive (only if user is near bottom)
  useEffect(() => {
    if (messages.length > prevMessagesLength.current && isNearBottom && !shouldMaintainScrollPosition.current) {
      scrollToBottom();
    }

    prevMessagesLength.current = messages.length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, isNearBottom]);

  // Initial scroll to bottom when messages first load
  useEffect(() => {
    if (messages.length > 0 && prevMessagesLength.current === 0) {
      scrollToBottom();
    }
  }, [messages.length, scrollToBottom]);

  return (
    <div className='w-full relative overflow-y-hidden h-full flex flex-1 flex-col'>
      <div ref={scrollRef} className='h-full overflow-y-auto px-6 py-4 space-y-4' onScroll={handleScroll}>
        {loadingMore && (
          <div className='flex justify-center'>
            <div className='flex items-center gap-2 text-gray-500'>
              <Loader2 className='h-4 w-4 animate-spin' />
              <span className='text-sm'>Loading messages...</span>
            </div>
          </div>
        )}
        {loadingMore && <ChatSkeleton visible={loading} />}
        {messages.map((message, index) => {
          const variant = getMessageVariant(message.senderId, selectedUser?.id || '');

          return (
            <MessageItem
              openModal={openModal}
              key={index}
              message={message}
              variant={variant}
              index={index}
              onActionClick={onActionClick}
              chatId={chatId}
              isLastMessage={index === messages.length - 1}
            />
          );
        })}
        <span ref={bottomSpan}></span>
      </div>
      {showScrollButton && (
        <Button
          onClick={() => {
            scrollToBottom();
          }}
          size='icon'
          disabled={loading || loadingMore}
          variant='outline'
          className='absolute text-primary bottom-2 left-1/2 transform
           -translate-x-1/2 inline-flex rounded-full shadow-md'
          aria-label='Scroll to bottom'
          color='light'
        >
          <ArrowDown className='size-4' />
        </Button>
      )}
    </div>
  );
};

MessageList.displayName = 'MessageList';
